import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface LandingPageContextValue {
  selectedCity: string | null;
  setSelectedCity: (city: string | null) => void;
  authModalOpen: boolean;
  setAuthModalOpen: (open: boolean) => void;
}

const LandingPageContext = createContext<LandingPageContextValue | null>(null);

export function LandingPageProvider({ children }: { children: ReactNode }) {
  const [selectedCity, setSelectedCityState] = useState<string | null>(null);
  const [authModalOpen, setAuthModalOpenState] = useState(false);

  const setSelectedCity = useCallback((city: string | null) => {
    setSelectedCityState(city?.trim() ? city.trim() : null);
  }, []);

  const setAuthModalOpen = useCallback((open: boolean) => {
    setAuthModalOpenState(open);
  }, []);

  const value = useMemo(
    () => ({
      selectedCity,
      setSelectedCity,
      authModalOpen,
      setAuthModalOpen,
    }),
    [selectedCity, setSelectedCity, authModalOpen, setAuthModalOpen],
  );

  return <LandingPageContext.Provider value={value}>{children}</LandingPageContext.Provider>;
}

export function useLandingPageContext(): LandingPageContextValue {
  const ctx = useContext(LandingPageContext);
  if (!ctx) {
    throw new Error('useLandingPageContext must be used within LandingPageProvider');
  }
  return ctx;
}

/** Safe accessor for components that may render outside the landing page provider. */
export function useLandingPageContextOptional(): LandingPageContextValue | null {
  return useContext(LandingPageContext);
}
